/**
 * 🚀 Unified Secure Logger - HIPAA Compliant Logging + Notifications
 *
 * Combines secure console logging with on-screen notifications
 * Matches backend logging format for perfect debugging consistency
 *
 * Features:
 * - HIPAA-compliant PHI sanitization
 * - Console logging (debug=dev only)
 * - Smart notifications for important logs
 * - Backend format compatibility
 * - Session tracking integration
 * - Performance monitoring
 * - Request correlation
 */

// Prevent double initialization
if (typeof window.UnifiedSecureLogger !== "undefined") {
	console.warn("UnifiedSecureLogger already initialized");
} else {
	// =============================================================================
	// UNIFIED LOGGER CONFIGURATION
	// =============================================================================

	const UNIFIED_CONFIG = {
		// Security (inherited from perfect logger)
		SECURITY: {
			REQUIRED_DEBUG_PARAM: "dev",
			PHI_SANITIZATION: true,
			PRODUCTION_SAFE: true
		},

		// Notification Rules - When to show notifications vs console only
		NOTIFICATION_RULES: {
			// Always show notifications for these levels
			ALWAYS_NOTIFY: ["error", "fatal"],

			// Show notifications for these contexts (regardless of level)
			NOTIFY_CONTEXTS: ["user-workflow", "payment", "eligibility", "appointment", "critical-flow", "user-action"],

			// Never show notifications for these contexts (console only)
			CONSOLE_ONLY_CONTEXTS: ["debug", "performance", "analytics", "state-change", "api-detail"],

			// Show notifications for messages containing these keywords
			NOTIFY_KEYWORDS: ["completed", "failed", "success", "error", "timeout", "connected", "processing"],

			// Notification display duration by level
			DURATION: {
				debug: 0, // No notifications for debug
				info: 3000, // 3 seconds
				warn: 5000, // 5 seconds
				error: 8000, // 8 seconds
				fatal: 0 // Persistent (manual close only)
			}
		},

		// Backend compatibility settings
		BACKEND_COMPAT: {
			// Session tracking
			SESSION_TRACKING: true,

			// Request correlation
			REQUEST_CORRELATION: true,

			// Performance timing
			PERFORMANCE_TIMING: true,

			// Log format matching
			CONSOLE_FORMAT: "backend-style", // or "frontend-style"

			// Context preservation
			PRESERVE_CONTEXT: true
		}
	};

	// =============================================================================
	// BACKEND-COMPATIBLE LOG LEVELS
	// =============================================================================

	const UNIFIED_LOG_LEVELS = {
		DEBUG: { priority: 0, name: "debug", emoji: "🔍", color: "#7bed9f", notifyByDefault: false },
		INFO: { priority: 1, name: "info", emoji: "ℹ️", color: "#3742fa", notifyByDefault: false },
		WARN: { priority: 2, name: "warn", emoji: "⚠️", color: "#ffa502", notifyByDefault: true },
		ERROR: { priority: 3, name: "error", emoji: "❌", color: "#ff4757", notifyByDefault: true },
		FATAL: { priority: 4, name: "fatal", emoji: "💀", color: "#2f1b69", notifyByDefault: true }
	};

	// =============================================================================
	// UNIFIED SECURE LOGGER CLASS
	// =============================================================================

	class UnifiedSecureLogger {
		constructor() {
			// Initialize base secure logger
			this.secureLogger = window.PerfectLogger || this.createFallbackLogger();
			this.isEnabled = this.secureLogger.isEnabled;

			// Initialize notification manager
			this.notificationManager = null;
			this.initializeNotificationManager();

			// Session tracking (backend compatible)
			this.currentSessionTrackingId = null;
			this.currentRequestId = null;
			this.requestStartTime = null;

			// Performance tracking
			this.performanceMetrics = new Map();

			// Initialize unified features
			this.initializeUnifiedFeatures();

			// Remote forwarding
			this.remoteConfig = this._loadRemoteConfig();
			this.remoteQueue = [];
			this._remoteFlushTimer = null;
			this._setupRemoteForwarding();

			// Log initialization if enabled
			if (this.isEnabled) {
				this.logInitialization();
			}
		}

		createFallbackLogger() {
			// Fallback if perfect logger isn't available
			return {
				isEnabled: false,
				log: () => {},
				info: () => {},
				warn: () => {},
				error: () => {},
				sanitizer: {
					sanitize: data => ({ data, sanitizationLog: [], wasSanitized: false })
				}
			};
		}

		initializeNotificationManager() {
			// Create notification manager with unified styling
			if (typeof window.NotificationManager !== "undefined") {
				this.notificationManager = new window.NotificationManager({
					position: "top-right",
					maxNotifications: 5,
					defaultDuration: 5000,
					consoleLogging: false, // We handle console logging
					userFriendlyMode: true,
					suppressDuplicates: true,
					enableDevTools: false // We provide our own dev tools
				});
			}
		}

		initializeUnifiedFeatures() {
			// Create unified API matching backend
			this.createUnifiedAPI();

			// Setup session tracking
			this.setupSessionTracking();

			// Setup performance timing
			this.setupPerformanceTiming();

			// Setup request correlation
			this.setupRequestCorrelation();
		}

		logInitialization() {
			console.log(
				"%c🚀 Unified Secure Logger Initialized",
				"background: linear-gradient(45deg, #667eea 0%, #764ba2 100%); color: white; padding: 8px 16px; border-radius: 8px; font-weight: bold; font-size: 14px;"
			);

			console.group("🔧 Unified Logger Configuration");
			console.log("🔒 Security:", "HIPAA Compliant - PHI Auto-Sanitization Active");
			console.log("📱 Debug Mode:", `debug=${UNIFIED_CONFIG.SECURITY.REQUIRED_DEBUG_PARAM} detected`);
			console.log("📺 Notifications:", this.notificationManager ? "✅ Active" : "❌ Unavailable");
			console.log("🔗 Backend Compatible:", "✅ Format Matching Active");
			console.log("📊 Session Tracking:", UNIFIED_CONFIG.BACKEND_COMPAT.SESSION_TRACKING ? "✅ Active" : "❌ Disabled");
			console.groupEnd();

			this.showAvailableCommands();
		}

		showAvailableCommands() {
			console.group("🎯 Available Commands (Backend Compatible)");
			console.log('📝 Basic: logger.info("message", "context", data)');
			console.log('🔗 Session: logger.setSessionTrackingId("session-id")');
			console.log('⏱️ Performance: logger.timeOperation("operation", "context", () => {})');
			console.log('📊 Flow: logger.trackFlow("flowName", "step", "context", data)');
			console.log('👤 Journey: logger.trackUserJourney("userId", "sessionId", "action", "context")');
			console.log('🔍 Request: logger.startRequest("requestId")');
			console.groupEnd();
		}

		// =============================================================================
		// BACKEND-COMPATIBLE API
		// =============================================================================

		createUnifiedAPI() {
			// Match backend signature; allow optional per-call options: { forward?: boolean }
			this.debug = (message, context, data, options) => this._unifiedLog("debug", message, context, data, options);
			this.info = (message, context, data, options) => this._unifiedLog("info", message, context, data, options);
			this.warn = (message, context, data, options) => this._unifiedLog("warn", message, context, data, options);
			this.error = (message, context, data, options) => this._unifiedLog("error", message, context, data, options);
			this.fatal = (message, context, data, options) => this._unifiedLog("fatal", message, context, data, options);
		}

		_unifiedLog(level, message, context = "", data = null, options = undefined) {
			if (!this.isEnabled) return;

			const levelInfo = UNIFIED_LOG_LEVELS[level.toUpperCase()] || UNIFIED_LOG_LEVELS.INFO;

			// Sanitize data for both console and notifications
			const sanitizedResult = this.secureLogger.sanitizer.sanitize(data, {
				sanitizationLevel: "strict",
				logSanitization: true
			});

			// Create unified log entry (backend compatible)
			const logEntry = {
				level: levelInfo.name,
				message,
				context: context || "",
				data: sanitizedResult.data,
				sanitizationLog: sanitizedResult.sanitizationLog,
				wasSanitized: sanitizedResult.wasSanitized,
				sessionTrackingId: this.currentSessionTrackingId,
				requestId: this.currentRequestId,
				timestamp: new Date().toISOString(),
				duration: this.requestStartTime ? Date.now() - this.requestStartTime : null
			};

			// Console logging (always, if debug=dev)
			this._logToConsole(logEntry);

			// Notification logic - smart decision making
			if (this._shouldShowNotification(levelInfo, context, message)) {
				this._showNotification(logEntry);
			}

			// Track performance if enabled
			if (UNIFIED_CONFIG.BACKEND_COMPAT.PERFORMANCE_TIMING) {
				this._trackPerformance(logEntry);
			}

			// Remote forwarding (allow per-call force)
			this._maybeEnqueueRemote(logEntry, Boolean(options && options.forward));

			return logEntry;
		}

		_shouldShowNotification(levelInfo, context, message) {
			if (!this.notificationManager) return false;

			// Check always notify levels
			if (UNIFIED_CONFIG.NOTIFICATION_RULES.ALWAYS_NOTIFY.includes(levelInfo.name)) {
				return true;
			}

			// Check console-only contexts (never notify)
			if (UNIFIED_CONFIG.NOTIFICATION_RULES.CONSOLE_ONLY_CONTEXTS.some(ctx => context.toLowerCase().includes(ctx.toLowerCase()))) {
				return false;
			}

			// Check notify contexts (always notify)
			if (UNIFIED_CONFIG.NOTIFICATION_RULES.NOTIFY_CONTEXTS.some(ctx => context.toLowerCase().includes(ctx.toLowerCase()))) {
				return true;
			}

			// Check notify keywords in message
			if (UNIFIED_CONFIG.NOTIFICATION_RULES.NOTIFY_KEYWORDS.some(keyword => message.toLowerCase().includes(keyword.toLowerCase()))) {
				return true;
			}

			// Default: use level's default behavior
			return levelInfo.notifyByDefault;
		}

		_logToConsole(logEntry) {
			// Backend-compatible console format
			const sessionPrefix = this.currentSessionTrackingId ? `[${this.currentSessionTrackingId}] ` : "";
			const levelInfo = UNIFIED_LOG_LEVELS[logEntry.level.toUpperCase()] || UNIFIED_LOG_LEVELS.INFO;
			const contextStr = logEntry.context ? `[${logEntry.context}]` : "";

			// Extract session info for filtering consistency
			const sessionInfo = this._extractSessionInfo(logEntry);
			const sessionStr = sessionInfo ? ` | ${sessionInfo}` : "";

			// Format message (backend style)
			if (logEntry.data && Object.keys(logEntry.data).length > 0) {
				const dataStr = typeof logEntry.data === "object" ? JSON.stringify(logEntry.data) : String(logEntry.data);
				const logMessage = `${sessionPrefix}${levelInfo.emoji} ${contextStr} ${logEntry.message}${sessionStr} | Data: ${dataStr}`;
				console.log(logMessage);
			} else {
				const logMessage = `${sessionPrefix}${levelInfo.emoji} ${contextStr} ${logEntry.message}${sessionStr}`;
				console.log(logMessage);
			}

			// Show sanitization warning if PHI was detected
			if (logEntry.wasSanitized) {
				console.warn(`🏥 PHI detected and sanitized in log data`);
			}
		}

		_extractSessionInfo(logEntry) {
			const parts = [];

			// Extract masterId from data
			if (logEntry.data && typeof logEntry.data === "object" && logEntry.data.masterId) {
				parts.push(`masterId:${logEntry.data.masterId}`);
			}

			// Extract requestId
			if (logEntry.requestId) {
				parts.push(`reqId:${logEntry.requestId.slice(-8)}`);
			}

			// Extract duration if available
			if (logEntry.duration) {
				parts.push(`duration:${logEntry.duration}ms`);
			}

			return parts.length > 0 ? parts.join(" ") : "";
		}

		_showNotification(logEntry) {
			if (!this.notificationManager) return;

			const levelInfo = UNIFIED_LOG_LEVELS[logEntry.level.toUpperCase()] || UNIFIED_LOG_LEVELS.INFO;
			const duration = UNIFIED_CONFIG.NOTIFICATION_RULES.DURATION[logEntry.level] || 5000;

			// Create user-friendly notification message
			let notificationMessage = logEntry.message;

			// Add context if important
			if (logEntry.context && !UNIFIED_CONFIG.NOTIFICATION_RULES.CONSOLE_ONLY_CONTEXTS.includes(logEntry.context)) {
				notificationMessage = `[${logEntry.context}] ${notificationMessage}`;
			}

			// Add data summary if relevant
			if (logEntry.data && this._isRelevantDataForNotification(logEntry.data)) {
				const dataSummary = this._createDataSummary(logEntry.data);
				if (dataSummary) {
					notificationMessage += `<br>${dataSummary}`;
				}
			}

			// Map log level to notification type
			const notificationType = this._mapLogLevelToNotificationType(logEntry.level);

			// Show notification
			this.notificationManager.show(notificationMessage, notificationType, levelInfo.name, duration);
		}

		_isRelevantDataForNotification(data) {
			if (!data || typeof data !== "object") return false;

			// Show data if it contains user-relevant information
			const relevantKeys = ["status", "progress", "error", "result", "nextStep", "duration"];
			return Object.keys(data).some(key => relevantKeys.some(relevant => key.toLowerCase().includes(relevant.toLowerCase())));
		}

		_createDataSummary(data) {
			if (!data || typeof data !== "object") return "";

			const summaryParts = [];

			if (data.status) summaryParts.push(`Status: ${data.status}`);
			if (data.progress) summaryParts.push(`Progress: ${data.progress}%`);
			if (data.duration) summaryParts.push(`Duration: ${data.duration}ms`);
			if (data.nextStep) summaryParts.push(`Next: ${data.nextStep}`);

			return summaryParts.join(" • ");
		}

		_mapLogLevelToNotificationType(level) {
			switch (level) {
				case "debug":
					return "info";
				case "info":
					return "info";
				case "warn":
					return "warning";
				case "error":
					return "error";
				case "fatal":
					return "error";
				default:
					return "info";
			}
		}

		// =============================================================================
		// REMOTE FORWARDING
		// =============================================================================

		_loadRemoteConfig() {
			const byName = name => {
				const el = document.querySelector(`meta[name="${name}"]`);
				return el ? el.getAttribute("content") : null;
			};

			// Defaults per request
			const defaultBase = (window.FUNNEL_CONFIG && window.FUNNEL_CONFIG.API_BASE_URL) || window.CURALIFE_BACKEND_BASE_URL || "https://admin.curalife.com";
			const defaultEndpoint = `${defaultBase.replace(/\/$/, "")}/api/logs/ingest`;
			const DEFAULT_ALLOW = ["user-workflow", "EligibilityService", "scheduling"];
			const DEFAULT_BLOCK = ["analytics", "performance", "dev-tools"];
			const DEFAULT_SAMPLE = 1;
			const DEFAULT_BATCH = 50;

			const overrides = window.LOG_FORWARDING || {};
			const cfg = {
				endpoint: overrides.endpoint || defaultEndpoint,
				minLevel: (overrides.minLevel || "info").toLowerCase(),
				allow: Array.isArray(overrides.allow) ? overrides.allow : DEFAULT_ALLOW,
				block: Array.isArray(overrides.block) ? overrides.block : DEFAULT_BLOCK,
				sample: typeof overrides.sample === "number" ? overrides.sample : DEFAULT_SAMPLE,
				batchSize: typeof overrides.batchSize === "number" ? overrides.batchSize : DEFAULT_BATCH,
				flushMs: typeof overrides.flushMs === "number" ? overrides.flushMs : 2000,
				env: byName("curalife:env") || overrides.env || undefined
			};

			const levels = { debug: 0, info: 1, warn: 2, error: 3, fatal: 4 };
			cfg._minLevelPriority = levels[cfg.minLevel] ?? 1;
			cfg.enabled = Boolean(cfg.endpoint);
			if (!(cfg.sample >= 0 && cfg.sample <= 1)) cfg.sample = DEFAULT_SAMPLE;
			if (!(cfg.batchSize > 0)) cfg.batchSize = DEFAULT_BATCH;
			if (!(cfg.flushMs > 0)) cfg.flushMs = 2000;
			return cfg;
		}

		_setupRemoteForwarding() {
			if (!this.remoteConfig.enabled) return;
			const flush = force => this._flushRemoteQueue(force);
			if (!this._remoteFlushTimer) this._remoteFlushTimer = setInterval(flush, this.remoteConfig.flushMs);
			window.addEventListener("visibilitychange", () => {
				if (document.visibilityState === "hidden") flush(true);
			});
			window.addEventListener("pagehide", () => flush(true));
			window.addEventListener("beforeunload", () => flush(true));
		}

		_shouldForward(entry) {
			if (!this.remoteConfig.enabled) return false;
			const levels = { debug: 0, info: 1, warn: 2, error: 3, fatal: 4 };
			const pr = levels[entry.level] ?? 1;
			// Forwarding is controlled by minLevel and filters; no debug flag required
			if (pr < this.remoteConfig._minLevelPriority) return false;
			if (this.remoteConfig.allow.length && !this.remoteConfig.allow.some(a => (entry.context || "").toLowerCase().includes(a.toLowerCase()))) return false;
			if (this.remoteConfig.block.length && this.remoteConfig.block.some(b => (entry.context || "").toLowerCase().includes(b.toLowerCase()))) return false;
			if (this.remoteConfig.sample < 1) {
				if (Math.random() > this.remoteConfig.sample) return false;
			}
			return true;
		}

		_maybeEnqueueRemote(entry, force) {
			try {
				if (!force && !this._shouldForward(entry)) return;
				const payload = {
					level: entry.level,
					message: entry.message,
					context: entry.context,
					data: entry.data,
					requestId: entry.requestId,
					searchableIds: this.currentSessionTrackingId ? { sessionTrackingId: this.currentSessionTrackingId } : undefined
				};
				this.remoteQueue.push(payload);
				if (this.remoteQueue.length >= this.remoteConfig.batchSize) this._flushRemoteQueue();
			} catch {}
		}

		_flushRemoteQueue(force) {
			if (!this.remoteConfig.enabled) return;
			if (!this.remoteQueue.length) return;
			const batch = this.remoteQueue.splice(0, this.remoteConfig.batchSize);
			try {
				const body = { batch, ts: Date.now(), src: "shopify-theme", env: this.remoteConfig.env };
				const opts = { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: !!force };
				fetch(this.remoteConfig.endpoint, opts).catch(() => {});
			} catch {}
		}

		_trackPerformance(logEntry) {
			const key = `${logEntry.context}_${logEntry.level}`;
			if (!this.performanceMetrics.has(key)) {
				this.performanceMetrics.set(key, { count: 0, totalTime: 0 });
			}

			const metrics = this.performanceMetrics.get(key);
			metrics.count++;
			if (logEntry.duration) {
				metrics.totalTime += logEntry.duration;
			}
		}

		// =============================================================================
		// BACKEND-COMPATIBLE SESSION TRACKING
		// =============================================================================

		setupSessionTracking() {
			// Match backend session tracking API
			this.setSessionTrackingId = sessionTrackingId => {
				this.currentSessionTrackingId = sessionTrackingId;
				this.info("Session tracking ID set", "session-tracking", { sessionTrackingId });
			};

			this.getSessionTrackingId = () => {
				return this.currentSessionTrackingId;
			};

			this.clearSessionTrackingId = () => {
				this.currentSessionTrackingId = null;
			};
		}

		// =============================================================================
		// BACKEND-COMPATIBLE REQUEST CORRELATION
		// =============================================================================

		setupRequestCorrelation() {
			this.startRequest = requestId => {
				const id = requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
				this.currentRequestId = id;
				this.requestStartTime = Date.now();

				this.debug("Request started", "request-correlation", { requestId: id });
				return id;
			};

			this.endRequest = () => {
				if (this.currentRequestId && this.requestStartTime) {
					const duration = Date.now() - this.requestStartTime;
					this.debug("Request ended", "request-correlation", {
						requestId: this.currentRequestId,
						duration: `${duration}ms`
					});
				}

				this.currentRequestId = null;
				this.requestStartTime = null;
			};
		}

		// =============================================================================
		// BACKEND-COMPATIBLE PERFORMANCE TIMING
		// =============================================================================

		setupPerformanceTiming() {
			this.timeOperation = async (operationName, context, operation) => {
				const start = Date.now();
				const result = operation();

				if (result instanceof Promise) {
					return result
						.then(res => {
							const duration = Date.now() - start;
							this.debug(`${operationName} completed`, context, { duration: `${duration}ms` });
							return res;
						})
						.catch(err => {
							const duration = Date.now() - start;
							this.error(`${operationName} failed`, context, {
								duration: `${duration}ms`,
								error: err.message || err
							});
							throw err;
						});
				} else {
					const duration = Date.now() - start;
					this.debug(`${operationName} completed`, context, { duration: `${duration}ms` });
					return result;
				}
			};
		}

		// =============================================================================
		// BACKEND-COMPATIBLE BUSINESS TRACKING
		// =============================================================================

		trackFlow(flowName, step, context, data = {}) {
			const enhancedData = {
				flowName,
				step,
				timestamp: new Date().toISOString(),
				...data
			};

			this.info(`[FLOW:${flowName}] ${step}`, context, enhancedData);
		}

		trackUserJourney(userId, sessionId, action, context, data = {}) {
			// Create privacy-safe identifiers (matching backend)
			const userHash = this._createHash(userId);
			const sessionHash = this._createHash(sessionId);

			this.info(`[USER_JOURNEY] ${action}`, context, {
				userHash,
				sessionHash,
				action,
				journey: true,
				...data
			});
		}

		_createHash(input) {
			// Simple hash for privacy (in real implementation, use proper hashing)
			let hash = 0;
			for (let i = 0; i < input.length; i++) {
				const char = input.charCodeAt(i);
				hash = (hash << 5) - hash + char;
				hash = hash & hash; // Convert to 32-bit integer
			}
			return `hash_${Math.abs(hash).toString(36)}`;
		}

		// =============================================================================
		// UTILITY METHODS
		// =============================================================================

		getStats() {
			return {
				session: {
					currentSessionTrackingId: this.currentSessionTrackingId,
					currentRequestId: this.currentRequestId,
					requestStartTime: this.requestStartTime
				},
				notifications: this.notificationManager
					? {
							active: this.notificationManager.notifications.length,
							total: this.notificationManager.notifications.length
						}
					: null,
				performance: Object.fromEntries(this.performanceMetrics),
				logger: this.secureLogger.showMetrics ? this.secureLogger.showMetrics() : null
			};
		}

		printStats() {
			console.group("📊 Unified Logger Statistics");
			console.table(this.getStats());
			console.groupEnd();
		}

		// Expose notification manager methods
		clearNotifications() {
			if (this.notificationManager) {
				this.notificationManager.clear();
			}
		}

		// Legacy compatibility
		log(message, data, options) {
			return this.debug(message, options?.context || "", data);
		}

		success(message, data, options) {
			return this.info(`✅ ${message}`, options?.context || "success", data);
		}
	}

	// =============================================================================
	// INITIALIZE & EXPORT
	// =============================================================================

	// Create global unified instance
	window.UnifiedSecureLogger = new UnifiedSecureLogger();

	// Create convenient exports that match backend exactly
	window.logger = {
		// Core logging methods (backend compatible)
		debug: (message, context, data, options) => window.UnifiedSecureLogger.debug(message, context, data, options),
		info: (message, context, data, options) => window.UnifiedSecureLogger.info(message, context, data, options),
		warn: (message, context, data, options) => window.UnifiedSecureLogger.warn(message, context, data, options),
		error: (message, context, data, options) => window.UnifiedSecureLogger.error(message, context, data, options),
		fatal: (message, context, data, options) => window.UnifiedSecureLogger.fatal(message, context, data, options),

		// Session tracking (backend compatible)
		setSessionTrackingId: id => window.UnifiedSecureLogger.setSessionTrackingId(id),
		getSessionTrackingId: () => window.UnifiedSecureLogger.getSessionTrackingId(),
		clearSessionTrackingId: () => window.UnifiedSecureLogger.clearSessionTrackingId(),

		// Request correlation (backend compatible)
		startRequest: id => window.UnifiedSecureLogger.startRequest(id),
		endRequest: () => window.UnifiedSecureLogger.endRequest(),

		// Business tracking (backend compatible)
		trackFlow: (flowName, step, context, data) => window.UnifiedSecureLogger.trackFlow(flowName, step, context, data),
		trackUserJourney: (userId, sessionId, action, context, data) => window.UnifiedSecureLogger.trackUserJourney(userId, sessionId, action, context, data),

		// Performance (backend compatible)
		timeOperation: (operationName, context, operation) => window.UnifiedSecureLogger.timeOperation(operationName, context, operation),

		// Utilities
		getStats: () => window.UnifiedSecureLogger.getStats(),
		printStats: () => window.UnifiedSecureLogger.printStats(),
		clearNotifications: () => window.UnifiedSecureLogger.clearNotifications(),

		// Legacy compatibility
		log: (message, data, options) => window.UnifiedSecureLogger.log(message, data, options),
		success: (message, data, options) => window.UnifiedSecureLogger.success(message, data, options),

		// Check if enabled
		isEnabled: window.UnifiedSecureLogger.isEnabled
	};

	// Backward compatibility shortcuts
	window.slog = window.logger.debug;
	window.sinfo = window.logger.info;
	window.swarn = window.logger.warn;
	window.serror = window.logger.error;

	// Advanced unified utilities
	window.logUtils = {
		stats: window.logger.getStats,
		printStats: window.logger.printStats,
		clearNotifications: window.logger.clearNotifications,
		setSession: window.logger.setSessionTrackingId,
		trackFlow: window.logger.trackFlow,
		timeOperation: window.logger.timeOperation
	};
} // End initialization guard
